import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { sessionActions } from 'store';
import { ErrorText, FindLoad, useModal } from 'utils';
import { CreateChancel, DateCalendarForm, SelectAutocompleteMultiple, TimeInput } from 'components';
import { CreateSession } from '../session/createSession';
import {
    apptTimeRange,
    DATE_FORMAT,
    MANUAL_SESSION_APPTS_ACTION,
    MANUAL_SESSION_LOAD_ACTION,
    MANUAL_SESSION_ORIGIN,
    manualSessionPath,
    TIME_FORMAT,
} from './constants';

const STEPS = { DETAILS: 'DETAILS', PREPARATION: 'PREPARATION' };

/**
 * "Add Data" from the Goals tab. Step 1 picks the date and, optionally, the appointment the data
 * belongs to; step 2 is the live Session Preparation (`CreateSession`), which picks, pins and orders
 * the goals. Continue opens the data-entry screen.
 *
 * Picking an appointment that already holds MANUAL data edits that session. An appointment with a
 * live-collected session is listed but disabled — the backend never edits those (409).
 */
export const AddManualData = ({ clientId }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { close } = useModal();
    const appts = useSelector((state) => state.session.manualSessionAppts);
    const manualSession = useSelector((state) => state.session.manualSession);
    const loadSession = FindLoad(MANUAL_SESSION_LOAD_ACTION);
    const [step, setStep] = useState(STEPS.DETAILS);
    // Wall-clock `HH:mm:ss` in the browser's zone. Only sent when no appointment is linked.
    const [times, setTimes] = useState({ startTime: null, endTime: null });
    const { control, watch, setValue, handleSubmit } = useForm({ defaultValues: { date: null, appt: null } });
    const date = watch('date');
    const appt = watch('appt');
    const today = moment().format(DATE_FORMAT);
    const editSessionId = appt?.sessionOrigin === MANUAL_SESSION_ORIGIN ? appt?.sessionId : null;

    // A linked appointment owns the session's times — they are shown, not edited.
    const shownTimes = appt
        ? { startTime: moment(appt.startTime).format('HH:mm'), endTime: moment(appt.endTime || appt.startTime).format('HH:mm') }
        : { startTime: times.startTime?.slice(0, 5), endTime: times.endTime?.slice(0, 5) };

    const timeError = useMemo(() => {
        if (appt || !date || !times.startTime || !times.endTime) return null;
        const at = (t) => moment(`${date} ${t}`, `${DATE_FORMAT} ${TIME_FORMAT}`);
        if (!at(times.endTime).isAfter(at(times.startTime))) return ErrorText.timeError;
        if (at(times.endTime).isAfter(moment())) return 'End Time cannot be in the future';
        return null;
    }, [appt, date, times]);

    useEffect(() => {
        dispatch(sessionActions.resetManualSession());
    }, [clientId]);

    useEffect(() => {
        setValue('appt', null);
        if (date) dispatch(sessionActions.getManualSessionAppts({ clientId, date }));
    }, [date]);

    useEffect(() => {
        if (editSessionId) dispatch(sessionActions.getManualSession(editSessionId));
    }, [editSessionId]);

    /** Goals that already hold data on the session being edited — pre-selected and locked, so no data is hidden. */
    const lockedGoalIds = useMemo(() => {
        if (!editSessionId || manualSession?.id !== editSessionId) return [];
        return (manualSession?.goals || [])
            .filter((g) => g?.goal?.targets?.some((t) => t?.tSummary?.trials?.length))
            .map((g) => g?.goal?.id);
    }, [editSessionId, manualSession]);

    const handleContinue = (goals) => {
        close();
        navigate(
            manualSessionPath(clientId, {
                date,
                time: appt ? null : times.startTime,
                endTime: appt ? null : times.endTime,
                apptId: appt?.id,
                sessionId: editSessionId,
            }),
            // `[{ goal, pinned }]` in the order picked in Session Preparation.
            { state: { goals } }
        );
    };

    // Step 2 is the live Session Preparation itself: same list, pin, drag-to-reorder, Select All
    // and the org's "include all active goals" lock. It hands the picked goals back instead of
    // creating a live session.
    if (step === STEPS.PREPARATION) {
        return (
            <div style={{ maxWidth: '100%' }}>
                <CreateSession
                    clientId={clientId}
                    lockedGoalIds={lockedGoalIds}
                    confirmText="Continue to Data Entry"
                    onCancel={() => setStep(STEPS.DETAILS)}
                    onConfirm={handleContinue}
                />
            </div>
        );
    }

    const apptOptionLabel = (i) => [i?.displayId, apptTimeRange(i), i?.staffName].filter(Boolean).join(' · ');

    return (
        <div style={{ width: 460, maxWidth: '100%' }}>
            <p className="modal-header-title">Add Manual Data</p>
            <form onSubmit={handleSubmit(() => setStep(STEPS.PREPARATION))}>
                <div className="modal-body-wrapper" style={{ gap: 16 }}>
                    <DateCalendarForm
                        name="date"
                        label="Data Date*"
                        control={control}
                        max={today}
                        rules={{ required: 'Data date is required.' }}
                    />

                    <SelectAutocompleteMultiple
                        name="appt"
                        label="Suggested Appointment"
                        placeholder={date ? (appts?.length ? 'Select an appointment (optional)' : 'No appointments on this date') : 'Pick a date first'}
                        control={control}
                        options={appts || []}
                        fullValue
                        disabled={!date || !appts?.length}
                        loadType={MANUAL_SESSION_APPTS_ACTION}
                        renderValue={apptOptionLabel}
                        getOptionDisabled={(i) => !i?.selectable}
                        renderCustomRow={(i) => (
                            <div>
                                <p>{apptOptionLabel(i)}</p>
                                {!i?.selectable ? (
                                    <p className="manual-appt-note">Data was collected live — edit it in the session screen.</p>
                                ) : i?.sessionOrigin === MANUAL_SESSION_ORIGIN ? (
                                    <p className="manual-appt-note">Has manually entered data — you will edit it.</p>
                                ) : null}
                            </div>
                        )}
                    />

                    <div className="manual-data-times">
                        {/* Keyed on the appointment: TimeInput reads `defaultValue` once, so it remounts to show new times. */}
                        <TimeInput
                            key={`start-${appt?.id || 'none'}`}
                            label="Start Time*"
                            name="startTime"
                            disabled={!!appt}
                            defaultValue={shownTimes.startTime}
                            errorFalse={true}
                            onChange={(e) => setTimes((prev) => ({ ...prev, startTime: e?.target?.value ? `${e.target.value}:00` : null }))}
                        />
                        <TimeInput
                            key={`end-${appt?.id || 'none'}`}
                            label="End Time*"
                            name="endTime"
                            disabled={!!appt}
                            defaultValue={shownTimes.endTime}
                            typeError={timeError || ''}
                            onChange={(e) => setTimes((prev) => ({ ...prev, endTime: e?.target?.value ? `${e.target.value}:00` : null }))}
                        />
                    </div>
                    {appt && <p className="manual-data-hint">Times come from the linked appointment.</p>}
                </div>

                {/* Disabled (not swapped for a loader) while the edited session loads — its goals feed `lockedGoalIds`. */}
                <CreateChancel
                    type="submit"
                    disabled={
                        !!loadSession?.length ||
                        !date ||
                        (!appt && (!times.startTime || !times.endTime || !!timeError)) ||
                        (appt && appt.selectable === false)
                    }
                    create={'Continue'}
                    chancel={'Cancel'}
                    onClose={close}
                    buttonWidth="100%"
                />
            </form>
        </div>
    );
};
